/**
 * Membership & classification override cards + Save (WSRADM-0015).
 * Seven keys only. No `productFamilyByCategory`. No page-level board Save.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ErrorDetails } from '@/components/ErrorDetails';
import { ApiError } from '@/api/errors';
import {
  DAY_COUNT_KEYS,
  FIELD_COPY_KEY,
  isMembershipKey,
  putMembershipConfig,
  type MembershipConfigDto,
  type MembershipField,
  type MembershipKey,
} from '@/api/configuration/membershipConfig';
import { useCopy } from '@/hooks/useCopy';

const MID = 'WStudio.membership.valueMidThreshold';
const HIGH = 'WStudio.membership.valueHighThreshold';
const ESTABLISHED = 'WStudio.membership.tenureEstablishedDays';
const LOYAL = 'WStudio.membership.tenureLoyalDays';

const FIELD_LABEL_FALLBACK: Record<MembershipKey, string> = {
  'WStudio.membership.gracePeriodDays': 'Grace period (days)',
  'WStudio.membership.shortTermThresholdDays': 'Short-term threshold (days)',
  'WStudio.membership.renewalDueSoonDays': 'Renewal due soon (days)',
  'WStudio.membership.tenureEstablishedDays': 'Established tenure (days)',
  'WStudio.membership.tenureLoyalDays': 'Loyal tenure (days)',
  'WStudio.membership.valueMidThreshold': 'Mid-value threshold',
  'WStudio.membership.valueHighThreshold': 'High-value threshold',
};

interface Draft {
  overriding: boolean;
  value: string;
  revert: boolean;
}

function emptyDrafts(): Record<string, Draft> {
  return {};
}

function hydrateDrafts(fields: MembershipField[]): Record<string, Draft> {
  const next = emptyDrafts();
  for (const field of fields) {
    next[field.key] = {
      overriding: field.overridden,
      value: field.overridden ? (field.derived ?? '') : '',
      revert: false,
    };
  }
  return next;
}

function isNonNegativeInt(raw: string): boolean {
  const trimmed = raw.trim();
  return /^(0|[1-9]\d*)$/.test(trimmed);
}

function isNonNegativeNumber(raw: string): boolean {
  const trimmed = raw.trim();
  if (trimmed === '') return false;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0;
}

function parseEffectiveNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

export interface MembershipSettingsProps {
  data: MembershipConfigDto;
  onSaved: (next: MembershipConfigDto) => void;
}

export function MembershipSettings({ data, onSaved }: MembershipSettingsProps) {
  const { t } = useCopy();
  const [drafts, setDrafts] = useState(() => hydrateDrafts(data.fields));
  const [saving, setSaving] = useState(false);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // The caught save error behind `saveError`, for the copyable details.
  const [saveFailure, setSaveFailure] = useState<unknown>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [serverField, setServerField] = useState<string | null>(null);

  useEffect(() => {
    setDrafts(hydrateDrafts(data.fields));
    setClientErrors({});
    setFormError(null);
    setServerField(null);
  }, [data]);

  const byKey = useMemo(() => {
    const map = new Map<string, MembershipField>();
    for (const field of data.fields) map.set(field.key, field);
    return map;
  }, [data.fields]);

  function draftFor(key: string): Draft {
    return drafts[key] ?? { overriding: false, value: '', revert: false };
  }

  function effective(key: string): string {
    const field = byKey.get(key);
    const draft = draftFor(key);
    if (draft.overriding) return draft.value;
    return field?.derived ?? '';
  }

  function setDraft(key: string, patch: Partial<Draft>) {
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...draftFor(key), ...patch },
    }));
    setSaveSuccess(false);
    setSaveError(null);
    setSaveFailure(null);
  }

  const midEffective = effective(MID).trim();
  const highEffective = effective(HIGH).trim();
  const valuePartial = (midEffective === '') !== (highEffective === '');

  function validate(): boolean {
    const next: Record<string, string> = {};
    let order: string | null = null;
    for (const [key, draft] of Object.entries(drafts)) {
      if (!draft.overriding) continue;
      if (!isMembershipKey(key)) continue;
      if ((DAY_COUNT_KEYS as readonly string[]).includes(key)) {
        if (!isNonNegativeInt(draft.value)) {
          next[key] = t('config.validation.nonNegativeInt', 'Enter a whole number of 0 or more.');
        }
      } else {
        const trimmed = draft.value.trim();
        if (trimmed !== '' && !isNonNegativeNumber(draft.value)) {
          next[key] = t('config.validation.nonNegativeNumber', 'Enter an amount of 0 or more.');
        }
      }
    }
    const midN = parseEffectiveNumber(effective(MID));
    const highN = parseEffectiveNumber(effective(HIGH));
    if (midN !== null && highN !== null && midN > highN) {
      order = t(
        'config.validation.valueThresholdOrder',
        'The mid-value threshold must be less than or equal to the high-value threshold.',
      );
    }
    const estN = parseEffectiveNumber(effective(ESTABLISHED));
    const loyalN = parseEffectiveNumber(effective(LOYAL));
    if (estN !== null && loyalN !== null && estN > loyalN) {
      order = t(
        'config.validation.tenureOrder',
        'The established-tenure threshold must be less than or equal to the loyal-tenure threshold.',
      );
    }
    setClientErrors(next);
    setFormError(order);
    return Object.keys(next).length === 0 && order === null;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    if (!validate()) return;
    const overrides: Array<{ key: string; value: string }> = [];
    const clearOverrides: string[] = [];
    for (const field of data.fields) {
      const draft = draftFor(field.key);
      if (draft.overriding) {
        overrides.push({ key: field.key, value: draft.value });
      } else if (draft.revert) {
        clearOverrides.push(field.key);
      }
    }
    const write =
      overrides.length === 0 && clearOverrides.length === 0
        ? {}
        : {
            ...(overrides.length > 0 ? { overrides } : {}),
            ...(clearOverrides.length > 0 ? { clearOverrides } : {}),
          };
    setSaving(true);
    setSaveError(null);
    setSaveFailure(null);
    setSaveSuccess(false);
    setServerField(null);
    try {
      const saved = await putMembershipConfig(write);
      onSaved(saved);
      setDrafts(hydrateDrafts(saved.fields));
      setSaveSuccess(true);
    } catch (err) {
      const message =
        err instanceof ApiError && typeof err.details?.['message'] === 'string'
          ? err.details['message']
          : t('config.membership.saveError', 'Could not save membership settings.');
      setSaveError(message);
      setSaveFailure(err);
      if (
        err instanceof ApiError &&
        err.details?.['code'] === 'INVALID_INTEGRATION_SETTINGS' &&
        typeof err.details['field'] === 'string' &&
        isMembershipKey(err.details['field'])
      ) {
        setServerField(err.details['field']);
      }
    } finally {
      setSaving(false);
    }
  }

  const dayFields = data.fields.filter((field) =>
    (DAY_COUNT_KEYS as readonly string[]).includes(field.key),
  );
  const midField = data.fields.find((field) => field.key === MID);
  const highField = data.fields.find((field) => field.key === HIGH);

  return (
    <Box component="form" onSubmit={onSubmit} noValidate>
      <Typography variant="h2" component="h2" sx={{ mb: 1 }}>
        {t('config.membership.title', 'Membership & classification')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          'config.membership.help',
          'These settings are read together to classify members for marketing.',
        )}
      </Typography>

      {saveSuccess ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('config.membership.saveSuccess', 'Settings saved.')}
        </Alert>
      ) : null}
      {saveError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('config.membership.saveError', 'Could not save membership settings.')}
          <ErrorDetails
            error={saveFailure}
            headline={t('config.membership.saveError', 'Could not save membership settings.')}
          />
        </Alert>
      ) : null}
      {formError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formError}
        </Alert>
      ) : null}

      <Stack spacing={2}>
        {dayFields.map((field) => (
          <FieldCard
            key={field.key}
            field={field}
            draft={draftFor(field.key)}
            error={
              clientErrors[field.key] ??
              (serverField === field.key ? (saveError ?? undefined) : undefined)
            }
            onOverride={() =>
              setDraft(field.key, { overriding: true, revert: false, value: field.derived ?? '' })
            }
            onRevert={() => setDraft(field.key, { overriding: false, revert: true, value: '' })}
            onValue={(value) => setDraft(field.key, { value })}
          />
        ))}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {midField ? (
            <Box sx={{ flex: 1 }}>
              <FieldCard
                field={midField}
                draft={draftFor(midField.key)}
                error={
                  clientErrors[midField.key] ??
                  (serverField === midField.key ? (saveError ?? undefined) : undefined)
                }
                onOverride={() =>
                  setDraft(midField.key, {
                    overriding: true,
                    revert: false,
                    value: midField.derived ?? '',
                  })
                }
                onRevert={() => setDraft(midField.key, { overriding: false, revert: true, value: '' })}
                onValue={(value) => setDraft(midField.key, { value })}
              />
            </Box>
          ) : null}
          {highField ? (
            <Box sx={{ flex: 1 }}>
              <FieldCard
                field={highField}
                draft={draftFor(highField.key)}
                error={
                  clientErrors[highField.key] ??
                  (serverField === highField.key ? (saveError ?? undefined) : undefined)
                }
                onOverride={() =>
                  setDraft(highField.key, {
                    overriding: true,
                    revert: false,
                    value: highField.derived ?? '',
                  })
                }
                onRevert={() =>
                  setDraft(highField.key, { overriding: false, revert: true, value: '' })
                }
                onValue={(value) => setDraft(highField.key, { value })}
              />
            </Box>
          ) : null}
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        {t(
          'config.membership.valueHelp',
          'Both thresholds must be set for value classification to run. Leaving them blank is a valid choice — no value classification is produced.',
        )}
      </Typography>
      {valuePartial ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t(
            'config.membership.valuePartial',
            'Only one threshold is set, so value classification stays off.',
          )}
        </Typography>
      ) : null}

      <Button type="submit" variant="contained" disabled={saving} sx={{ mt: 3 }}>
        {saving
          ? t('config.membership.saving', 'Saving…')
          : t('config.membership.save', 'Save')}
      </Button>
    </Box>
  );
}

function FieldCard({
  field,
  draft,
  error,
  onOverride,
  onRevert,
  onValue,
}: {
  field: MembershipField;
  draft: Draft;
  error: string | undefined;
  onOverride: () => void;
  onRevert: () => void;
  onValue: (value: string) => void;
}) {
  const { t } = useCopy();
  const key = isMembershipKey(field.key) ? field.key : null;
  const label = key
    ? t(FIELD_COPY_KEY[key], FIELD_LABEL_FALLBACK[key])
    : field.key;
  const overriding = draft.overriding;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" sx={{ mb: 1, alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1">{label}</Typography>
        <Chip
          size="small"
          label={
            overriding
              ? t('config.membership.overriding', 'Overriding')
              : t('config.membership.default', 'Default')
          }
        />
      </Stack>
      {overriding ? (
        <TextField
          fullWidth
          value={draft.value}
          onChange={(event) => onValue(event.target.value)}
          error={error !== undefined}
          helperText={error}
        />
      ) : (
        <TextField
          fullWidth
          label={t('config.membership.derived', 'Derived value')}
          value={field.derived ?? ''}
          slotProps={{ input: { readOnly: true } }}
          error={error !== undefined}
          helperText={error}
        />
      )}
      <Box sx={{ mt: 1 }}>
        {overriding ? (
          <Button size="small" onClick={onRevert}>
            {t('config.membership.revert', 'Revert to default')}
          </Button>
        ) : (
          <Button size="small" onClick={onOverride}>
            {t('config.membership.override', 'Override')}
          </Button>
        )}
      </Box>
    </Paper>
  );
}
